import { Brand, BrandType, SipDetails } from '../brand.types';
import { GroupByType, PeriodType } from '../brandOwner';
import {
  AgentDetails,
  AutoPayoutType,
  Configuration,
  LocationEntity,
  LocationMode,
  LocationPayoutService,
  Owner,
  PayoutConfigResponse,
  PayoutModelType,
  SalesConfig,
  SimpleLocationEntity,
} from '../location.types';
import { MenuInfo, SimpleMenuProviderEntity } from '../menu.types';
import {
  Coordinates,
  LocationImageEntity,
  Provider,
  ProviderConnectionStatus,
  ProviderStatus,
  ProviderType,
  ProviderValue,
  SalesStatics,
} from '../shared.types';
import { SyncStatus } from './menu.types';
import { CreateTaxRateDto } from '../taxRate.types';

export type SimpleLocationWithTodaysDataResponse = {
  location: SimpleLocationEntity;
  todaysSales: number;
  todaysOrders: number;
};

export type LocationVerificationCountResponse = {
  locations: number;
  agents: number;
  vDrive: number;
  brands: number;
};

export type ProviderValueResponse = {
  provider: Provider;
  value: number;
};

export type SalesStaticResponse = {
  count: ProviderValueResponse[];
  total: ProviderValueResponse[];
  virtual: ProviderValueResponse[];
  cancelled: ProviderValueResponse[];
  tax: ProviderValueResponse[];
  recovered: ProviderValueResponse[];
};

export type DashboardResponse = {
  locationSales: SalesStaticResponse;
  brandSales: SalesStaticResponse;
};

export type BrandTypeAndSalesStatisticResponse = {
  type: BrandType;
  sales: SalesStaticResponse;
};

export type DetailsSalesStatisticResponse = {
  totalCombined: ProviderValue[];
  total: ProviderValue[];
  count: ProviderValue[];
  virtual: ProviderValue[];
  virtualCount: ProviderValue[];
  recovered: ProviderValue[];
  taxWithoutVR: ProviderValue[];
};

export type DetailsBrandsSalesStatisticResponse = {
  type: BrandType;
  total: ProviderValueResponse[];
  count: ProviderValueResponse[];
};

export type PrintingSettingsRequestData = {
  message: string | null;
  frontCopies: number;
  backCopies: number;
  onlyBrandReceipt: boolean;
  includePreparationNotes: boolean;
};

export type PreviewReceiptSizeResponse = {
  url: string;
  size: number;
};

export type PayoutProviderRuleDto = {
  /** Providers this exception applies to. At least one. */
  providers: ProviderType[];
  /**
   * Order sources this exception is limited to. Omitted or empty means every
   * source - so always send back what the GET returned, even untouched, or an
   * exception meant for one connection quietly starts covering all of them.
   */
  sources?: ProviderConnectionType[];
  /** Location's share for these providers, 0-100. */
  percentage?: number | null;
  /** Flat fee vGrubs charges for these providers, in cents. */
  flatFee?: number | null;
};

export type PayoutTierDto = {
  /** Lower bound of the base amount window, in cents (inclusive). */
  minAmount: number;
  /** Upper bound in cents (exclusive). `null` = no upper bound. */
  maxAmount?: number | null;
  /** Location's share in this tier, 0-100. `null` = no bracket in this tier. */
  percentage?: number | null;
  /** Flat fee vGrubs charges in this tier, in cents. `null` = no flat fee. */
  flatFee?: number | null;
  /** Per-provider exceptions inside this tier. Sending fewer removes the rest. */
  providerRules?: PayoutProviderRuleDto[];
};

export type PayoutConfigDto = {
  service: LocationPayoutService;
  model: PayoutModelType;
  /** Required for `bracket`, optional for `hybrid`. */
  percentage?: number | null;
  /** Required for `flat_fee`, optional for `hybrid`. In cents. */
  flatFee?: number | null;
  /** Only used by the hybrid model. Must not overlap. */
  tiers?: PayoutTierDto[];
  /**
   * Per-provider exceptions. Only the `vGrubs` service accepts them. A write is
   * a full replace, so always send the complete list - omitted rules are gone.
   */
  providerRules?: PayoutProviderRuleDto[];
};

export type UpdatePayoutConfigsRequestData = {
  /** Services left out keep their current config. */
  configs: PayoutConfigDto[];
};

export type VerifyLocationRequestData = {
  /** Services left out start on the platform defaults. */
  payoutConfigs?: PayoutConfigDto[];
  crmApiKey?: string;
  sip?: SipDetails;
  achCustomerId?: string;
  /** Whether the restaurant runs on a tablet or on a POS. */
  mode?: LocationMode;
  /**
   * The location's taxes, written in the same transaction as the verification.
   * A location can no longer be verified without at least one, and at least
   * one of them has to carry `applyToAllItems` - see
   * `validateVerificationTaxRates`.
   */
  taxRates: CreateTaxRateDto[];
  salesConfig?: null | {
    monthlyFeeId: null | number;
    vDriveDeliveryChargeFee: null | number;
    vGrubsService: null | number;
    extraTablets: null | number;
    extraPrinters: null | number;
    totalAmount: null | number;
    vRevenueService: null | number;
    revenueRecoveryFee: null | number;
  };
};

export type ProviderAndProviderStatusResponse = Array<{
  provider: Provider;
  status: ProviderStatus;
  connectionStatus: ProviderConnectionStatus;
  connectionType: ProviderConnectionType;
  haveMenuSet?: boolean | null;
  haveWorkingHoursSet?: boolean | null;
  inProgressConnectAllowed: boolean;
  shouldAskForMenu: boolean;
  syncStatus?: SyncStatus | null;
  id: number;
  storeLink: string | null;
  /**
   * When a pause made from our side ends (ISO). Null when we did not pause it,
   * so an `offline` row with null is offline for another reason. Absent from
   * older API builds. Nothing announces the end: a past value is not paused.
   */
  pausedUntil?: string | null;
}>;

/**
 * How long a provider pause lasts. `pauseUntil` is `"HH:mm"` or
 * `"YYYY-MM-DDTHH:mm"` read in the LOCATION's zone, at most tomorrow 11:59 PM.
 * `minutes: null` (or neither) is the rest of today, to midnight there.
 */
export type ProviderPauseLength = (
  | { minutes: number | null }
  | { pauseUntil: string }
) & {
  /**
   * Start the pause later instead of now, same format as `pauseUntil`. The
   * answer is the same as for a pause now, and the provider stays online
   * until it starts. A past one (or within a minute) starts it now.
   */
  pauseFrom?: string;
};

/**
 * A pause set to start later, from `GET .../provider/scheduled-pauses`. The
 * list is the location's AND every brand's, earliest first, only the ones not
 * started yet: once one starts, is cancelled or fails it is gone.
 */
export type ScheduledPause = {
  id: number;
  locationId: number;
  /** null = the whole location (its own brand is sent as null too). */
  brandId: number | null;
  brandName: string | null;
  /** null = every provider (Pause All). */
  provider: Provider | null;
  /** ISO with the location's offset. */
  startAt: string;
  endAt: string;
  source: 'customer' | 'admin' | 'chat';
  createdBy: string | null;
};

export enum ProviderConnectionType {
  thirdParty = 'thirdParty',
  direct = 'direct',
  foodhub = 'foodhub',
}

export type ProviderAndConnectionStatus = {
  isConnected: boolean;
  provider: Provider;
};

export type ProviderAndConnectionStatusResponse =
  Array<ProviderAndConnectionStatus>;

export type PrintingSettingsResponse = {
  message: string | null;
  image: string | null;
  frontCopies: number;
  backCopies: number;
  onlyBrandReceipt: boolean;
  includePreparationNotes: boolean;
};

export type UpdateLocationRequestData = Pick<
  LocationEntity,
  | 'payoutDelayHours'
  | 'name'
  | 'referralPercentage'
  | 'crmApiKey'
  | 'achCustomerId'
  | 'sip'
  | 'menuConsultant'
> & {
  /** Services left out keep their current config. */
  payoutConfigs?: PayoutConfigDto[];
  referralId: number | null;
  menuManager: string | null;
  menuAssistant: string | null;
  menuPictureAssistant: string | null;
  autoPayoutType: AutoPayoutType;
  includeServerFeeDeduction: boolean;
  includeDiscountDeduction: boolean;
  includeVDrivePayoutDeduction: boolean;
  reportBasedPayout: boolean;
  vOrdersOrderingUrl: string;
  phoneNumber?: string | null;
  /**
   * Only ever `tablet`. POS mode is set by connecting a POS, so asking for it
   * here is a 400 - as is asking for tablet while a POS connection still
   * exists. Left out, the mode stays as it is.
   */
  mode?: LocationMode;
};

export type ConnectProviderRequestData = {
  provider: Provider;
  storeId?: string | null;
  redirectUrl?: string | null;
  shouldUseFoodhub?: boolean;
};

export type SetLocationCredentialsRequestData = {
  provider: Provider;
  storeId?: string | null;
  username?: string | null;
  password?: string | null;
};

export type IncompleteLocation = {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipCode: string | null;
  streetNumber: string | null;
  // Suite / unit inside a plaza, kept out of `address` on purpose. `null` when
  // Google has no subpremise for the place.
  unitNumber: string | null;
  coordinates: Coordinates;
};

export type CreateCompleteLocation = {
  id: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  streetNumber: string;
  // The address is replaced as a whole, so a missing/empty unitNumber wipes the
  // stored one - always send what the form holds.
  unitNumber: string | null;
  coordinates: Coordinates;
};

export type UpsertProviderContactRequestData = {
  before: number;
  after: number;
  provider: Provider;
};

export type UpdateLocationOwnershipRequestData = {
  ownerId: string | number;
  clearBalance: boolean;
  removeExistingPartner: boolean;
};

export type UpdateLocationConfigRequestData = {
  einLast4?: string;
  bankAccountLast4?: string;
};

export type LocationEarningPlan = AgentDetails;

export type UpdateLocationEarningsPlanRequestData = {
  earningPlans: {
    id?: number | null;
    year?: number | null;
    amount: number;
  }[];
};

export type LocationAgentServiceKey = 'vGrubs' | 'vDrive' | 'vOrders';

export type LocationAgentShare = {
  agentId: number;
  userId: number;
  name: string;
  isReferral: boolean;
  percentage: number;
  amount: number;
};

export type LocationServiceAgents = {
  enabled: boolean;
  total: number;
  agents: LocationAgentShare[];
};

export type LocationAgentsEarningPlans = {
  vGrubs: LocationServiceAgents;
  vDrive: LocationServiceAgents;
  vOrders: LocationServiceAgents;
};

// Full list of agents assigned to a location (incl. those on no service).
export type LocationAgent = {
  agentId: number;
  userId: number;
  name: string;
  isReferral: boolean;
  onVGrubs: boolean;
  onVDrive: boolean;
  onVOrders: boolean;
  vGrubsPercentage: number | null;
  vDrivePercentage: number | null;
  vOrdersPercentage: number | null;
};

export type SaveLocationAgentShareItem = {
  agentId: number;
  vGrubsAmount: number | null;
  vDriveAmount: number | null;
  vOrdersAmount: number | null;
};

export type SaveLocationAgentSharesRequestData = {
  agents: SaveLocationAgentShareItem[];
};

export type UpdateLocationSalesRequestData = {
  monthlyFeeId: number | null;
  vDriveDeliveryChargeFee: number | null;
  vGrubsService: number | null;
  extraTablets: number | null;
  extraPrinters: number | null;
  totalAmount: number | null;
  vRevenueService: number | null;
  revenueRecoveryFee: number | null;
  /**
   * Cents 1-500 = vTablet billed per order, -1 = back to monthly, left out =
   * unchanged. Requires vOrders; turning vOrders off (`monthlyFeeId: -1`)
   * clears it on its own.
   */
  vOrdersPerOrderFee?: number;
  /**
   * Cents 1-500 = vTablet billed per day from the vGrubs balance, -1 = off,
   * left out = unchanged. One of per order and per day: both set (neither
   * -1) is a 400.
   */
  vOrdersPerDayFee?: number;
};

/** One month of the per-order fee suggestion's order history. */
export type ServiceFeeSuggestionMonth = {
  /** yyyy-MM, New York. */
  month: string;
  /** The running month, only part of it is in. */
  partial: boolean;
  orders: number;
  /** Average payout per order before the service fee, cents. */
  averagePayout: number;
  /** Orders paying out less than the recommended fee (it is capped at the payout). */
  ordersBelowRecommended: number;
  /** What the recommended fee would have collected that month, cents. */
  collectedAtRecommended: number;
};

/**
 * `GET /v1/locations/:id/sales-config/per-order-fee-suggestion`. The amount is
 * computed by the backend from the location's virtual-brand orders; the AI
 * only writes `summary`, `reasoning` and `risks`. All money in cents.
 */
export type ServiceFeeSuggestion = {
  /** The monthly amount the per-order fee should add up to, the one used. */
  monthlyTarget: number;
  /**
   * Where `monthlyTarget` came from: the `target` we sent, the location's
   * saved monthly vTablet fee, or the standard $89 when it has neither.
   */
  targetSource: 'request' | 'monthlyFee' | 'default';
  /**
   * The SAVED vTablet switch; the suggestion is given either way. Not shown:
   * the button lives under the form's switch, which is on whenever it is
   * clicked, saved or not.
   */
  vTabletEnabled: boolean;
  /** 1-500; null when the location has no virtual-brand orders. */
  recommendedFee: number | null;
  /** False when even $5 per order would not have reached the target. */
  targetReachable: boolean;
  baselineMonths: string[];
  baselineMonthlyOrders: number;
  expectedMonthlyCollection: number;
  worstMonthCollection: number;
  bestMonthCollection: number;
  /** The running month projected to a full one; null on its first day. */
  currentMonthProjectedOrders: number | null;
  /** Null = billed monthly now. */
  currentPerOrderFee: number | null;
  /** Debt repayment plan's per-order amount, taken after this fee. */
  debtPlanPerOrderAmount: number | null;
  months: ServiceFeeSuggestionMonth[];
  summary: string;
  reasoning: string[];
  /** May be empty. */
  risks: string[];
};

export type CreateWorkingHoursRequestData = {
  provider: Provider;
  business: {
    day: number;
    startTime: string;
    endTime: string;
  };
  delivery: {
    day: number;
    startTime: string;
    endTime: string;
  };
};

export type CreateStaffMemberRequestData = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

export type CustomerLocationEntity = {
  id: number;
  storeId: number;
  verified: boolean;
  payoutConfigs: PayoutConfigResponse[];
  coordinates: Coordinates;
  owner: Owner;
  configuration: Configuration;
  name: string;
  address: string;
  unitNumber: string | null;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phone: string;
  menuSignInProviders: SimpleMenuProviderEntity[];
  businessLicense: string;
  crmApiKey: string | null;
  brands: Brand[];
  images: LocationImageEntity[];
  providers: Provider[];
  notes: string;
  earningsAvailable: boolean;
  createdAt: string;
  achCustomerId: string | null;
  menuConsultant: string | null;
  menuInfo: MenuInfo;
  salesConfig: SalesConfig;
  payoutDelayHours: number;
  salesStatistics: SalesStatics;
  mode: LocationMode;
};

export type GetLocationsOverviewParams = {
  periodType: PeriodType;
  from?: string | Date;
  to?: string | Date;
  timeZone: string;
  groupByType: GroupByType;
};

export type PosStatusResponse = Array<{
  type: PosType;
  status: ProviderStatus;
  connectionStatus: ProviderConnectionStatus;
  connectionType: PosConnectionType;
}>;

export enum PosType {
  clover = 'clover',
  toast = 'toast',
  lightspeedO = 'lightspeedO',
  lightspeedK = 'lightspeedK',
  square = 'square',
  foodhub = 'foodhub',
  threeS = 'threeS',
  eposNow = 'eposNow',
}

export enum PosConnectionType {
  foodhub = 'foodhub',
}

export type ConnectPOSDto = {
  provider: PosType;
  redirectUrl?: string | null;
};

export type DisconnectPOSDto = {
  provider: PosType;
};

export type TrackingSmsType = 'tablet' | 'printer';

export type LocationTrackingSmsEntity = {
  id: number;
  trackingLink: string;
  type: TrackingSmsType;
  createdAt: string;
};
